import { useMemo } from 'react';

import type {
  CreateFunctionCommand as CreateFunctionPayload,
  DeleteFunctionCommand as DeleteFunctionPayload,
  CreateEndFunctionCommand as EndFunctionCreatePayload,
  UpdateEndFunctionCommand as EndFunctionUpdatePayload,
  CreateStartFunctionCommand as StartFunctionCreatePayload,
  UpdateStartFunctionCommand as StartFunctionUpdatePayload,
  UpdateFunctionCommand as UpdateFunctionPayload,
  UpdateLatencyControl as UpdateLatencyControlPayload,
} from '@polyai/schemas';
import {
  createFunction as createFunctionAction,
  deleteFunction as deleteFunctionAction,
  updateFunction as updateFunctionAction,
  updateLatencyControl as updateLatencyControlAction,
} from '@polyai/stream/store/reducers/functions/actions';
import { selectFunctions } from '@polyai/stream/store/reducers/functions/selectors';
import type { Function } from '@polyai/stream/store/reducers/functions/types';
import {
  createEndFunction as createEndFunctionAction,
  createStartFunction as createStartFunctionAction,
  deleteEndFunction,
  deleteStartFunction,
  updateEndFunction as updateEndFunctionAction,
  updateStartFunction as updateStartFunctionAction,
} from '@polyai/stream/store/reducers/specialFunctions/actions';

import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import { sortByDate } from 'utils/date';

export const useStreamFunctions = (enabled: boolean = true) => {
  const { isProjectResourceEnabled } = useProjectResource();
  const isEnabled =
    enabled &&
    (isProjectResourceEnabled(PROJECT_RESOURCE.HANDOFFS) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.KNOWLEDGE_BASE) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.RULES));

  const allFunctions: Function[] = useAppSelector(state =>
    selectFunctions(state),
  );

  const { startFunction, endFunction } = useAppSelector(
    state => state.specialFunctions,
  );

  const activeFunctions: Function[] = useMemo(
    () =>
      allFunctions
        .filter(f => f !== startFunction && f !== endFunction && !f.archived)
        .sort((a, b) => sortByDate(a.updatedAt, b.updatedAt)),
    [allFunctions, startFunction, endFunction],
  );

  const serverDispatch = useServerDispatch();

  const createFunction = async (data: CreateFunctionPayload) => {
    const action = createFunctionAction(data);
    await serverDispatch(action);
  };

  const updateFunction = async (data: UpdateFunctionPayload) => {
    const action = updateFunctionAction(data);
    await serverDispatch(action);
  };

  const createStartFunction = async (data: StartFunctionCreatePayload) => {
    const action = createStartFunctionAction(data);
    await serverDispatch(action);
  };

  const createEndFunction = async (data: EndFunctionCreatePayload) => {
    const action = createEndFunctionAction(data);
    await serverDispatch(action);
  };

  const updateStartFunction = async (data: StartFunctionUpdatePayload) => {
    const action = updateStartFunctionAction(data);
    await serverDispatch(action);
  };

  const updateEndFunction = async (data: EndFunctionUpdatePayload) => {
    const action = updateEndFunctionAction(data);
    await serverDispatch(action);
  };

  const deleteFunction = async (data: DeleteFunctionPayload) => {
    const action = deleteFunctionAction(data);
    await serverDispatch(action);
  };

  const deleteSpecialFunction = async (
    data: DeleteFunctionPayload,
    isStartFunction: boolean,
    isEndFunction: boolean,
  ) => {
    let actionWrapper:
      | typeof deleteStartFunction
      | typeof deleteEndFunction
      | undefined = undefined;
    if (isStartFunction) {
      actionWrapper = deleteStartFunction;
    } else if (isEndFunction) {
      actionWrapper = deleteEndFunction;
    }

    if (actionWrapper !== undefined) {
      const action = actionWrapper(data);
      await serverDispatch(action);
    }
  };

  const updateLatencyControl = async (data: UpdateLatencyControlPayload) => {
    const action = updateLatencyControlAction(data);
    await serverDispatch(action);
  };

  return {
    isEnabled,
    allFunctions,
    createFunction,
    updateFunction,
    createStartFunction,
    createEndFunction,
    updateStartFunction,
    updateEndFunction,
    deleteFunction,
    startFunction,
    endFunction,
    activeFunctions,
    updateLatencyControl,
    deleteSpecialFunction,
  };
};
